import { isValid } from 'date-fns'
import { TFunction } from 'i18next'

import { Organization } from '@modules-deprecated/app/organization/types/organization'

import { Language } from '../../../../types/language'
import { convertDateToUTC } from '../../../../utils'
import { ZervantSettingsFields } from '../../../invoicingSettings/types/zervantSettingsFields'
import { DateOfSaleOrServiceRule } from '../../enums/dateOfSaleOrServiceRule'
import { InvoiceBillingStage } from '../../enums/invoiceBillingStage'
import { InvoiceDiscountLocation } from '../../enums/invoiceDiscountLocation'
import { InvoiceDiscountVat } from '../../enums/invoiceDiscountVat'
import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { InvoiceType } from '../../enums/invoiceType'
import { DepositInvoiceConfig } from '../../types/invoiceDeposit/depositInvoiceConfig'
import { InvoiceForm } from '../../types/invoiceForm/invoiceForm'
import { InvoiceFormLine } from '../../types/invoiceForm/invoiceFormLine/invoiceFormLine'
import { InvoiceLineColumn } from '../../types/invoiceLineColumn'
import { InvoicePayloadFields } from '../../types/invoicePayload/invoicePayloadFields'
import { getInvoiceDocumentTitle } from '../getInvoiceDocumentTitle'
import { getInvoiceNumberDisplayedValue } from '../getInvoiceNumberDisplayedValue'
import { hasInvoiceLineDiscount } from '../hasInvoiceLineDiscount'
import { isDocumentTypeQuote } from '../isDocumentTypeQuote'
import { isDocumentTypeRecurringInvoice } from '../isDocumentTypeRecurringInvoice'
import { isInvoiceFormRowLine } from '../isInvoiceFormRowLine'

const documentTypesWithDateOfSaleOrService = [
  InvoiceDocumentType.CreditNote,
  InvoiceDocumentType.Invoice,
  InvoiceDocumentType.Quote,
]

interface GetFieldsPayloadOptions {
  approvedEstimateId?: string
  approvedInvoiceId?: string
  billingStage?: InvoiceBillingStage
  depositConfig?: DepositInvoiceConfig
  documentType: InvoiceDocumentType
  invoiceValues: InvoiceForm
  invoiceLanguage: Language
  organization?: Organization
  t: TFunction
  visibleColumns: InvoiceLineColumn[]
  settingsFields?: ZervantSettingsFields
}

interface IsInvoiceVatNotLiableOptions extends Pick<GetFieldsPayloadOptions, 'documentType' | 'organization'> {
  invoiceLines: InvoiceFormLine[]
}

const isInvoiceVatNotLiable = ({ documentType, invoiceLines, organization }: IsInvoiceVatNotLiableOptions) => {
  let isVatNoLiable = false

  if (organization?.isVatExempted) {
    const isAnyLineWithPositiveVatRate = invoiceLines.some(
      (line) => isInvoiceFormRowLine(line) && typeof line?.vatRatePercent === 'number' && line.vatRatePercent > 0,
    )
    const shouldHideVatOnCreditNote = !isAnyLineWithPositiveVatRate
    isVatNoLiable = documentType === InvoiceDocumentType.CreditNote ? shouldHideVatOnCreditNote : true
  }

  return isVatNoLiable
}

export const getFieldsPayload = ({
  approvedEstimateId,
  approvedInvoiceId,
  billingStage,
  depositConfig,
  documentType,
  invoiceValues: formValues,
  invoiceLanguage,
  organization,
  t,
  visibleColumns,
  settingsFields,
}: GetFieldsPayloadOptions): InvoicePayloadFields => {
  const {
    buyerReference,
    currency,
    dateOfSaleOrService,
    discount,
    discountType,
    dueDate,
    exchangeRate,
    footerNote,
    invoiceLines,
    invoiceNumber,
    invoiceNumberPrefix,
    issueDate,
    language,
    message,
    paymentTerm,
    purchaseOrderNumber,
  } = formValues

  const isQuote = isDocumentTypeQuote(documentType)
  const invoiceNumberValue = getInvoiceNumberDisplayedValue(invoiceNumber, invoiceNumberPrefix)
  const title = getInvoiceDocumentTitle({ documentType, language, t })
  const isCustomerNumberVisible = settingsFields?.customerNumber?.visible
  const isBuyerReferenceVisible = settingsFields?.buyerReference?.visible
  const termsAndConditions = settingsFields?.legalterms?.default || ''
  const isTermsAndConditionsVisible = settingsFields?.legalterms?.visible
  const isVatNotLiable = isInvoiceVatNotLiable({ documentType, invoiceLines, organization })
  const shouldIncludeClientReferences = !isQuote
  const hasLineDiscount = hasInvoiceLineDiscount(invoiceLines)
  const isGlobalDiscount = !hasLineDiscount && discount > 0
  const shouldIncludeDiscountFields = discountType !== undefined && (hasLineDiscount || isGlobalDiscount)
  const shouldIncludeDateOfSaleOrService =
    dateOfSaleOrService && isValid(dateOfSaleOrService) && documentTypesWithDateOfSaleOrService.includes(documentType)
  const documentNumber = isQuote ? String(invoiceNumber ?? '') : invoiceNumberValue
  const isRecurringInvoice = isDocumentTypeRecurringInvoice(documentType)

  // Transform 'total' to 'grossTotal' in visible columns because of backend discrepancy:
  // 'total' in column visibility settings = 'grossTotal' in invoice draft columns fields
  const transformedVisibleColumns = visibleColumns.map((key) => (key === 'total' ? 'grossTotal' : key))

  return {
    columns: {
      name: 'columns',
      value: transformedVisibleColumns,
    },
    currency: {
      name: 'currency',
      value: currency,
    },
    customerNumber: {
      name: 'customerNumber',
      visible: isCustomerNumberVisible,
    },
    description: {
      name: 'description',
      value: message,
    },
    dueDate: {
      name: 'dueDate',
      value:
        dueDate && isValid(dueDate)
          ? convertDateToUTC(dueDate).toISOString()
          : convertDateToUTC(new Date()).toISOString(),
      visible: true,
    },
    footerText: {
      name: 'footerText',
      value: footerNote,
    },
    invoiceNumber: {
      name: 'invoiceNumber',
      value: documentNumber,
    },
    issueDate: {
      name: 'issueDate',
      value:
        issueDate && isValid(issueDate)
          ? convertDateToUTC(issueDate).toISOString()
          : convertDateToUTC(new Date()).toISOString(),
    },
    language: {
      name: 'language',
      value: invoiceLanguage,
    },
    legalterms: {
      name: 'legalterms',
      value: termsAndConditions,
      visible: isTermsAndConditionsVisible,
    },
    locale: {
      name: 'locale',
      value: language,
    },
    paymentTime: {
      name: 'paymentTime',
      value: paymentTerm,
    },
    title: {
      name: 'title',
      value: title,
    },
    typeName: {
      name: 'typeName',
      value: isVatNotLiable ? InvoiceType.SALES_VATNOTLIABLE : InvoiceType.SALES,
    },
    // Conditional fields
    ...(shouldIncludeClientReferences && {
      buyerReference: {
        name: 'buyerReference',
        value: buyerReference,
        visible: isBuyerReferenceVisible,
      },
      purchaseOrderNumber: {
        name: 'purchaseOrderNumber',
        value: purchaseOrderNumber,
      },
    }),
    ...(shouldIncludeDateOfSaleOrService && {
      dateOfSaleOrService: {
        name: 'dateOfSaleOrService',
        value: convertDateToUTC(dateOfSaleOrService).toISOString(),
        visible: true,
      },
    }),
    ...(isRecurringInvoice && {
      recurringProfileEnabled: {
        name: 'recurringProfileEnabled',
        value: true,
        visible: true,
      },
      dateOfSaleOrServiceRule: {
        name: 'dateOfSaleOrServiceRule',
        value: DateOfSaleOrServiceRule.SameAsInvoiceDate,
      },
    }),
    // Only include if they have a value
    ...(exchangeRate && {
      exchangeRate: {
        name: 'exchangeRate',
        value: exchangeRate,
      },
    }),
    ...(approvedInvoiceId && {
      approvedInvoiceId: {
        name: 'approvedInvoiceId',
        value: Number(approvedInvoiceId),
      },
    }),
    ...(approvedEstimateId && {
      approvedEstimateId: {
        name: 'approvedEstimateId',
        value: Number(approvedEstimateId),
      },
    }),
    ...(billingStage && {
      billingStage: {
        name: 'billingStage',
        value: billingStage,
      },
    }),
    ...(billingStage === InvoiceBillingStage.Deposit &&
      depositConfig && {
        deposit: {
          name: 'deposit',
          value: {
            amount: depositConfig.amount,
            amountType: depositConfig.type,
          },
        },
      }),
    ...(shouldIncludeDiscountFields && {
      discountAmount: {
        name: 'discountAmount',
        value: isGlobalDiscount ? discount : 0,
      },
      discountEnabled: {
        name: 'discountEnabled',
        value: true,
      },
      discountLocation: {
        name: 'discountLocation',
        value: isGlobalDiscount ? InvoiceDiscountLocation.PerTotal : InvoiceDiscountLocation.PerLine,
      },
      discountType: {
        name: 'discountType',
        value: discountType,
      },
      discountVAT: {
        name: 'discountVAT',
        value: InvoiceDiscountVat.PreVat, // discounts are calculated preVat only
      },
    }),
  }
}
